/**
 * Super simple queue implementation
 */
export default class GenericQueue<T> {
    private queue: T[];
    private observers: GenericQueueObserver[];

    constructor(queue?: T[]) {
        this.queue = queue || [];
        this.observers = [];
    }

    addObserver(observer: GenericQueueObserver) {
        this.observers.push(observer);
    }

    setQueue(queue: T[]) {
        this.queue = queue;
    }

    enqueue(item: T | T[]) {
        if (Array.isArray(item)) {
            item.forEach(individualItem => this.queue.push(individualItem));
        }
        else {
            this.queue.push(item);
        }
        this.notifyAll();
    }

    dequeue(): T | undefined {
        return this.queue.shift();
    }

    enqueueFirst(t: T) {
        this.queue.unshift(t);
        this.notifyAll();
    }

    isEmpty(): boolean {
        return this.queue.length === 0;
    }

    size(): number {
        return this.queue.length;
    }

    elements(): T[] {
        return this.queue;
    }

    clear() {
        this.queue = [];
    }

    private notifyAll() {
        this.observers.forEach(o => o.onData());
    }
}

export interface GenericQueueObserver {
    onData(): void;
}

/**
 * Utility class that provides a way to process a queue of items sequentially.
 */
export class GenericQueueProcessor<T> implements GenericQueueObserver {
    private observedQueue: GenericQueue<T>;
    private running: boolean = false;
    private processor: (item: T) => Promise<void>;
    private pauseWhenPredicate: () => boolean;
    private optimizeThreshold: number | null = null;
    private optimizeFunction: ((items: T[]) => T[]) | null = null;

    constructor(observedQueue: GenericQueue<T>) {
        this.observedQueue = observedQueue;
        this.pauseWhenPredicate = () => false;
        this.observedQueue.addObserver(this);
    }

    /**
     * Starts the processing of the queue, if it is not already running, if a proper processor has been set, and if the
     * queue is not empty.
     *
     */
    start() {
        if (!this.running && !this.observedQueue.isEmpty() && this.processor) {
            this.running = true;
            (async () => await this.processQueue())().then(() => {
                this.running = false;
            });
        }

    }

    /**
     * Sets a predicate that will pause the processing of the queue when it returns true.
     */
    pauseWhen(predicate: () => boolean): this {
        this.pauseWhenPredicate = predicate;
        return this;
    }

    /**
     * Stops the processing of the queue. Please note that this will not clear the queue, and the processing will resume
     * when the start method is called again.
     */
    stop() {
        this.running = false;
    }

    /**
     * Sets the processor function that will be called for each item in the queue.
     */
    runOnEachItem(processor: (item: T) => Promise<void>): this {
        this.processor = processor;
        return this;
    }

    /**
     * Sets an optimization function that will be applied when the queue size reaches the specified threshold.
     * This allows for batching or reducing queue items for more efficient processing.
     *
     * @param threshold The minimum queue size required to trigger optimization
     * @param optimizeFunction A function that takes all queue items and returns optimized items
     * @returns this instance for method chaining
     */
    optimize(threshold: number, optimizeFunction: (items: T[]) => T[]): this {
        this.optimizeThreshold = threshold;
        this.optimizeFunction = optimizeFunction;
        return this;
    }

    /**
     * Callback method that is called when the observed queue has new data.
     */
    onData() {
        this.start();
    }

    /**
     * Processes the queue sequentially. Will only process the next item if the processor is set, the queue is not
     * empty, and the pauseWhenPredicate returns false. Before processing, checks if optimization should be applied.
     */
    private async processQueue(): Promise<void> {
        return (async () => {
            let processNext = async () => {
                while (this.running && !this.observedQueue.isEmpty() && !this.pauseWhenPredicate()) {
                    // Check if optimization should be applied
                    if (this.optimizeThreshold !== null &&
                        this.optimizeFunction !== null &&
                        this.observedQueue.size() >= this.optimizeThreshold) {
                        // Extract all items from the queue
                        const allItems: T[] = [];
                        while (!this.observedQueue.isEmpty()) {
                            const item = this.observedQueue.dequeue();
                            if (item) {
                                allItems.push(item);
                            }
                        }

                        // Apply optimization function
                        const optimizedItems = this.optimizeFunction(allItems);

                        // Put optimized items back into the queue
                        this.observedQueue.setQueue(optimizedItems);
                    }
                    // Process items one by one
                    if (!this.observedQueue.isEmpty()) {
                        let item = this.observedQueue.dequeue();
                        if (item) {
                            await this.processor(item);
                        }
                    }
                }
                this.running = false;
            };
            await processNext();
        })();
    }
}
